import { useDispatch, useSelector } from "react-redux";
import AceEditor from "react-ace";
import { useCallback, useEffect, useState, useMemo } from "react";
import Modal from "../../../components/organisms/Modal";
import { MODALS } from "../../../components/organisms/AppModals/modals.constants";
import { actions as modalActions } from "../../../components/organisms/Modal/modal.reducers";
import { deleteIcon } from "../../../assets/icons";
import { Table } from "../../../components/molecules";
import {
  isPlainObject,
  unset,
  isObject,
  mapValues,
  isArray,
  remove,
} from "lodash";
import {
  EMPTY_ARRAY,
  EMPTY_STRING,
  DATA_TYPES,
  MODAL_SIZE,
  MODAL_POSITION,
  ACE_EDITOR_THEMES,
  ONE,
  ZERO,
} from "../../../constants";
import { START_NODE_NAME } from "../datamodels.constants";
import "ace-builds/src-min-noconflict/ext-language_tools";
import "ace-builds/src-min-noconflict/mode-json";
import "ace-builds/src-min-noconflict/theme-twilight";
import { selectSelectedNode } from "../dataModels.selectors";

const { setSelectedModal } = modalActions;
const { DARK } = ACE_EDITOR_THEMES;
const { LG } = MODAL_SIZE;
const { CENTER } = MODAL_POSITION;
const { OBJECT } = DATA_TYPES;
const OBJECT_LIST = "Object list";
const OBJECT_TYPE = "Object";

const getFormattedResponseData = (
  obj,
  keyAddress = EMPTY_STRING,
  currentIndent = ZERO
) => {
  const newTableData = [];
  obj &&
    Object.entries(obj).forEach(([key, val]) => {
      const updatedKeyAddress = keyAddress ? `${keyAddress}.${key}` : key;
      let indent = currentIndent;

      if (Array.isArray(val) && typeof val[ZERO] === OBJECT) {
        indent += ONE;
        const updatedObj = {
          id: key,
          fieldName: key,
          sampleValue: EMPTY_STRING,
          fieldType: OBJECT_LIST,
          indent,
          keyAddress: updatedKeyAddress,
        };
        newTableData.push(updatedObj);
        val.forEach((item, index) =>
          newTableData.push(
            ...getFormattedResponseData(
              item,
              `${updatedKeyAddress}[${index}]`,
              indent
            )
          )
        );
      } else if (typeof val === OBJECT) {
        indent += ONE;
        const updatedObj = {
          id: key,
          fieldName: key,
          sampleValue: EMPTY_STRING,
          fieldType: OBJECT_TYPE,
          indent,
          keyAddress: updatedKeyAddress,
        };
        newTableData.push(updatedObj);
        newTableData.push(
          ...getFormattedResponseData(val, updatedKeyAddress, indent)
        );
      } else {
        indent += ONE;
        const updatedObj = {
          id: key + val,
          fieldName: key,
          sampleValue: val,
          fieldType: typeof val,
          indent,
          keyAddress: updatedKeyAddress,
        };
        newTableData.push(updatedObj);
      }
      indent -= ONE;
    });
  return newTableData;
};

const CustomModelDefinition = ({ name, value, handleChange, title }) => {
  const dispatch = useDispatch();
  const { name: selectedNodeName } = useSelector(selectSelectedNode);
  const [shouldShowModal, setShouldShowModal] = useState(false);
  const [inputData, setInputData] = useState(EMPTY_STRING);
  const [tableData, setTableData] = useState(EMPTY_ARRAY);
  const [isValidJSON, setIsValidJSON] = useState(true);
  const [shouldShowEditor, setShouldShowEditor] = useState(!value);
  const [initialValue, setInitialValue] = useState(value);
  const label = initialValue ? `Edit ${title}` : `Define ${title}`;

  const isStartNode = useMemo(() => {
    return selectedNodeName === START_NODE_NAME;
  }, [selectedNodeName]);

  useEffect(() => {
    if (value) {
      if (Array.isArray(value)) setTableData(value);
      else setTableData(getFormattedResponseData(value));
      setInputData(JSON.stringify(value));
    }
  }, [value]);

  useEffect(() => {
    try {
      const isvalidJson = isPlainObject(JSON.parse(inputData));
      setIsValidJSON(isvalidJson);
    } catch (e) {
      setIsValidJSON(false);
    }
  }, [inputData]);

  const handleOpenModal = useCallback(() => {
    dispatch(setSelectedModal(MODALS.CUSTOM_MODEL_DEFINITION_MODAL));
    setShouldShowModal(true);
    setShouldShowEditor(!value);
  }, [dispatch, value]);

  const handleClose = useCallback(() => {
    dispatch(setSelectedModal(null));
    setShouldShowModal(false);
    setInitialValue(value);
  }, [dispatch, value]);

  const handleOnSubmit = () => {
    const updatedData = isObject(inputData) ? inputData : JSON.parse(inputData);
    handleChange(updatedData, name);
    dispatch(setSelectedModal(null));
    setShouldShowModal(false);
  };

  const deleteProperty = (key) => {
    const updatedJson = isObject(inputData) ? inputData : JSON.parse(inputData);
    Array.isArray(updatedJson)
      ? remove(updatedJson, ({ keyAddress, fieldName }) =>
          keyAddress ? keyAddress === key : fieldName === key
        )
      : unset(updatedJson, key);
    const updatedJsonString = JSON.stringify(updatedJson);
    setInputData(updatedJsonString);
    setTableData(
      Array.isArray(updatedJson)
        ? updatedJson
        : getFormattedResponseData(updatedJson)
    );
  };

  const updateNestedArrays = (obj) => {
    return mapValues(obj, (value) => {
      if (isArray(value) && value.length) {
        return [value[0]];
      } else if (isObject(value)) {
        return updateNestedArrays(value);
      }
      return value;
    });
  };

  const handleOnNext = () => {
    let parsedData = JSON.parse(inputData);
    const updatedData = updateNestedArrays(parsedData);
    setTableData(getFormattedResponseData(updatedData));
    setShouldShowEditor(false);
    setInputData(
      isStartNode ? getFormattedResponseData(updatedData) : updatedData
    );
  };

  const deleteIconFormatter = (row) => (
    <div className="w-4 h-4 m-auto">
      <img
        src={deleteIcon}
        alt="delete-icon"
        className="cursor-pointer"
        onClick={() => deleteProperty(row.keyAddress || row.fieldName)}
      />
    </div>
  );

  const indentation = (row) => (
    <div style={{ marginLeft: row.indent * 18 }}>{row.fieldName}</div>
  );

  const columns = [
    {
      text: "Field name",
      formatter: (row) => indentation(row),
    },
    {
      text: "Sample Value",
      dataField: "sampleValue",
    },
    {
      text: "Field type",
      dataField: "fieldType",
      className: "capitalize",
    },
    {
      formatter: (row) => deleteIconFormatter(row),
    },
  ];

  const footer = (
    <div className="p-6 border-t border-gray-700 text-right">
      {!shouldShowEditor && (
        <button
          className="btn btn-primary-light mr-1"
          type="button"
          onClick={() => {
            setInputData(EMPTY_STRING);
            setShouldShowEditor(true);
            setInitialValue(EMPTY_STRING);
          }}
        >
          Define New Model
        </button>
      )}
      {shouldShowEditor && (
        <button
          className="btn btn-primary-light mr-1"
          type="button"
          onClick={() => {
            if (value) {
              setInitialValue(value);
            }
            setShouldShowEditor(false);
          }}
        >
          Previous
        </button>
      )}
      {shouldShowEditor ? (
        <button
          className="btn btn-primary"
          type="button"
          onClick={handleOnNext}
          disabled={!isValidJSON}
        >
          Next
        </button>
      ) : (
        <button
          className="btn btn-primary"
          type="button"
          onClick={handleOnSubmit}
        >
          Submit
        </button>
      )}
    </div>
  );

  return (
    <>
      <button
        className="btn btn-primary h-auto w-full"
        type="button"
        onClick={handleOpenModal}
      >
        {label}
      </button>
      {shouldShowModal && (
        <Modal
          title={label}
          footer={footer}
          handleClose={handleClose}
          size={LG}
          position={CENTER}
        >
          {shouldShowEditor ? (
            <div>
              <AceEditor
                mode="json"
                theme={DARK}
                name="aceEditor"
                className="ace-editor max-w-full"
                height="430px"
                width="800px"
                defaultValue=""
                value={inputData}
                focus={true}
                onChange={setInputData}
                setOptions={{
                  showGutter: true,
                  enableBasicAutocompletion: true,
                  enableLiveAutocompletion: true,
                  fontSize: 14,
                  highlightActiveLine: true,
                  tabSize: 4,
                  useWorker: false,
                }}
              />
              {inputData && !isValidJSON && (
                <div className="absolute text-red-500">
                  Invalid JSON document
                </div>
              )}
            </div>
          ) : (
            <div className="min-h-[330px] w-full max-h-[58vh] overflow-y-scroll scrollbar">
              <Table
                columns={columns}
                data={tableData}
                isCustomModelData={true}
              />
            </div>
          )}
        </Modal>
      )}
    </>
  );
};
export default CustomModelDefinition;
